/* Входы «Фотосессии товара» ↔ вид формы движка шаблона (план вехи 3, шаг 2 экрана; план shoot §2.4–2.5). Чистые функции без DOM и запросов:
   - pshootLists: документ экрана (GET /app/studio/screens/pshoot) и форма сервера → списки в виде шаблона (CATS, SCENES, SETS, MPS, FMTS, PLAT,
     NS, THEMES, WISH), значения по умолчанию, пределы, цена за кадр, пример пустой рамки; категории, площадки, форматы и число фото — только
     опубликованные полем формы (у родной формы — те же правила: pshoot-model.ts, PShootOrderForm.tsx);
   - readPshoot / writePshoot: входы ↔ вид (категория, набор, свой набор, свои фото сцен, число фото, площадка, формат, модель, пожелания) —
     только опубликованными ключами и тем же кодированием, что родная форма (shoot/kit/form-model.ts encode, splitScenes; модель —
     model / person_id / avatar_id / me, как pickModel), поэтому заявки обеих форм читаются друг другом (I1, откат). Слоты фото товара
     (photo…photo_5) и своих фото сцен (reference_1…9) пишут мосты загрузок (media-bridge, ref-bridge), «Съёмку» (shoot) — cam-bridge. */
import { studioFields, type StudioForm } from '../studio/studio-api';
import { artworkUrl } from '../studio/studio-art-model';
import { record, rows, text } from '../studio/studio-content';
import { pickFrom, type ModelChoice, type SnapPick } from '../studio/engine-kit/people-bridge';
import { allowed, decode, encode, fieldOf, limitOf, photoSlots, unitPrice, valueOf, type Inputs } from '../shoot/kit/form-model';
import { pshootKeys, splitScenes } from './pshoot-model';

/** Сцена в виде шаблона (SCENES): m — модель (0 — без людей, 1 — по желанию, 2 — сцена с моделью), b — метка, sp — фон-заглушка
    (рисунка сцены у сервера нет: кадр — его картинка url, примеры — ex) */
export interface EngineScene {
  id: string;
  cat: string;
  n: string;
  d: string;
  th: string[];
  m: number;
  pop: number;
  b: string;
  isNew: boolean;
  main: boolean;
  sp: { k: string; c: string[]; hz: number; y: number };
  url: string;
  ex: string[];
}
/** Набор в виде шаблона (SETS[категория]) */
export interface EngineSet {
  id: string;
  n: string;
  sub: string;
  sc: string[];
  b: string;
}
/** Площадка или формат строкой PLAT шаблона: [ключ, название, пропорции, ширина, высота, подпись своего формата?] */
export type PlatRow = [string, string, string, number, number, string?];
export interface PshootLists {
  /** CATS: [ключ, название, товар в именительном] */
  cats: string[][];
  scenes: EngineScene[];
  sets: Record<string, EngineSet[]>;
  /** MPS: [ключ, название] */
  mps: string[][];
  /** FMTS: [ключ, подпись, пропорции, ширина, высота, для чего] */
  fmts: Array<[string, string, string, number, number, string]>;
  plat: PlatRow[];
  counts: number[];
  themes: string[];
  /** WISH: [подпись, фраза] */
  wishes: string[][];
  defaults: { cat: string; set: string; n: number; plat: string; fmt: string };
  limits: { files: number; scenes: number; wishes: number; side: number };
  /** C за кадр (null — сервер не публикует) и слово единицы */
  unit: number | null;
  unitWord: string;
  /** Пример пустой рамки «Ваше фото · В сцене» — только картинки сервера, иначе null */
  example: { before: string; after: string; labels: string[]; title: string; text: string } | null;
  /** Пример пустой рамки своей категории (categories[].example: before_url, after_url; exURL шаблона — товар категории): нет — общий example */
  examples: Record<string, { before: string; after: string }>;
}
/** Вид формы: то, что из входов ложится в S движка */
export interface PshootView {
  cat: string;
  /** набор категории: id набора, 'own' — «Свой набор» */
  set: string;
  /** сцены «Своего набора» */
  own: string[];
  /** выбраны свои фото сцен (set_id 'ref') */
  ref: boolean;
  n: number;
  plat: string;
  fmt: string;
  pick: SnapPick;
  wishes: string;
}

/** Фон-заглушка сцены (в серверном режиме сцену рисует картинка сервера, а не SVG шаблона) */
const PLAIN = { k: 'sweep', c: ['#ffffff', '#ffffff', '#ffffff', '#ffffff'], hz: 0.66, y: 0.8 };
/** Форматы словами, если документ их не называет (родная форма: FORMAT_NAMES) */
const FORMAT_ROWS: Record<string, [string, string, number, number, string]> = {
  mp: ['Как у площадки', '3:4', 1200, 1600, 'Карточка маркетплейса'],
  sq: ['Квадрат', '1:1', 1200, 1200, 'Для соцсетей и Ozon Fresh'],
  p45: ['Пост', '4:5', 1080, 1350, 'Для ленты соцсетей'],
  st: ['Сторис', '9:16', 1080, 1920, 'На весь экран телефона'],
};
const SET_MAX = 12;
const idOf = (value: unknown) => (typeof value === 'number' ? String(value) : text(value));
const strings = (value: unknown) => (Array.isArray(value) ? value.map(idOf).filter(Boolean) : []);
const positive = (value: unknown, fallback: number) => (typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? value : fallback);
const image = (value: unknown) => (typeof value === 'string' && value ? (artworkUrl(value) ?? '') : '');
const badge = (item: Record<string, unknown>) => (item.hit === true ? 'Хит' : item.new === true ? 'Новое' : '');
/** «1200 × 1600» → [1200, 1600] */
const sizeOf = (value: unknown): [number, number] | null => {
  const m = /(\d+)\s*[×x]\s*(\d+)/.exec(text(value));
  return m ? [Number(m[1]), Number(m[2])] : null;
};
/** Только опубликованные значения поля (поля нет или значений нет — все); пусто после сужения — все */
function narrow<T>(list: T[], id: (item: T) => string, form: StudioForm | null, key: string | undefined): T[] {
  const field = key ? fieldOf(form, key) : undefined;
  if (!field?.values.length) return list;
  const kept = list.filter(item => field.values.includes(id(item)));
  return kept.length ? kept : list;
}
/** Поле формы как его прислал сервер (default_value, min, max, max_items, prices — fieldOf их не несёт) */
export const rawField = (form: StudioForm | null | undefined, key: string | undefined): Record<string, unknown> =>
  key ? record(studioFields(form).find(field => field.key === key)) : {};
/** default_value поля формы (строкой) */
const fieldDefault = (form: StudioForm | null, key: string | undefined) => {
  const raw = rawField(form, key).default_value;
  return raw === undefined || raw === null ? '' : String(raw);
};

/** Документ экрана и форма → списки шаблона */
export function pshootLists(doc: Record<string, unknown>, form: StudioForm | null): PshootLists {
  const keys = pshootKeys(form);
  const allCats = rows(doc.categories).flatMap(item => (idOf(item.id) && text(item.name) ? [[idOf(item.id), text(item.name), text(item.item) || text(item.name)]] : []));
  const cats = narrow(allCats, item => item[0], form, keys.category);
  const firstOf = new Set<string>();
  const scenes: EngineScene[] = rows(doc.scenes).flatMap(item => {
    const id = idOf(item.id);
    if (!id || !text(item.name)) return [];
    const cat = idOf(item.category) || idOf(item.category_id);
    const model = text(item.model);
    const main = item.main === true || (!firstOf.has(cat) && item.main !== false);
    firstOf.add(cat);
    const themes = strings(item.themes);
    return [
      {
        id,
        cat,
        n: text(item.name),
        d: text(item.description),
        th: themes.length ? themes : [''],
        m: model === 'required' ? 2 : model === 'optional' ? 1 : item.people === true || item.with_model === true ? 2 : 0,
        pop: Number(item.popularity) || 0,
        b: badge(item),
        isNew: item.new === true,
        main,
        sp: { ...PLAIN, c: PLAIN.c.slice() },
        url: image(item.image_url ?? item.cover_url ?? item.url),
        ex: (Array.isArray(item.examples) ? item.examples : []).map(image).filter(Boolean),
      },
    ];
  });
  const known = new Set(scenes.map(scene => scene.id));
  const allSets = rows(doc.sets).flatMap(item => {
    const id = idOf(item.id);
    if (!id || !text(item.name)) return [];
    const sc = (Array.isArray(item.scenes) ? item.scenes : [])
      .map(scene => idOf(scene && typeof scene === 'object' ? ((scene as Record<string, unknown>).id ?? (scene as Record<string, unknown>).scene_id) : scene))
      .filter(scene => known.has(scene));
    return sc.length ? [{ cat: idOf(item.category) || idOf(item.category_id), set: { id, n: text(item.name), sub: text(item.sub), sc, b: badge(item) } }] : [];
  });
  const sets: Record<string, EngineSet[]> = {};
  for (const [cat] of cats) sets[cat] = allSets.filter(item => !item.cat || item.cat === cat).slice(0, SET_MAX).map(item => ({ ...item.set, sc: item.set.sc.slice() }));
  const platforms = narrow(
    rows(doc.platforms).flatMap(item => (idOf(item.id) ? [item] : [])),
    item => idOf(item.id),
    form,
    keys.platform,
  );
  const mps = platforms.map(item => [idOf(item.id), text(item.name) || idOf(item.id)]);
  const docFormats = rows(doc.formats).flatMap(item => {
    const id = idOf(item.id);
    if (!id) return [];
    const base = FORMAT_ROWS[id];
    const size = sizeOf(item.size);
    return [
      [
        id,
        text(item.name) || base?.[0] || id,
        text(item.ratio) || base?.[1] || '3:4',
        positive(item.width, size?.[0] ?? base?.[2] ?? 1200),
        positive(item.height, size?.[1] ?? base?.[3] ?? 1600),
        text(item.sub) || base?.[4] || '',
      ] as [string, string, string, number, number, string],
    ];
  });
  const formatField = keys.format ? fieldOf(form, keys.format) : undefined;
  const fmts = formatField?.values.length
    ? formatField.values.map(value => docFormats.find(item => item[0] === value) ?? ([value, ...(FORMAT_ROWS[value] ?? [value, '3:4', 1200, 1600, ''])] as [string, string, string, number, number, string]))
    : docFormats;
  const plat: PlatRow[] = platforms
    .map(item => {
      const size = sizeOf(item.size);
      return [idOf(item.id), text(item.name) || idOf(item.id), text(item.ratio) || '3:4', size?.[0] ?? 1200, size?.[1] ?? 1600] as PlatRow;
    })
    .concat(fmts.slice(1).map(([k, n, a, w, h, s]) => [k, n, a, w, h, s] as PlatRow));
  const countField = keys.count ? fieldOf(form, keys.count) : undefined;
  const fromField = (countField?.values ?? []).map(Number).filter(n => Number.isSafeInteger(n) && n > 0);
  const counts = fromField.length ? fromField : (Array.isArray(doc.counts) ? doc.counts : []).map(Number).filter(n => Number.isSafeInteger(n) && n > 0);
  const chips = rows(doc.wish_chips).flatMap(chip => (text(chip.label) ? [[text(chip.label), text(chip.text) || text(chip.label)]] : []));
  const themes = strings(doc.themes);
  const pick = (want: string[], list: string[]) => want.find(value => !!value && list.includes(value)) ?? list[0] ?? '';
  const catIds = cats.map(item => item[0]);
  const cat = pick([fieldDefault(form, keys.category), text(doc.default_category)], catIds);
  const nDefault = Number(fieldDefault(form, keys.count));
  const preset = Number.isSafeInteger(nDefault) && nDefault > 0 && (!counts.length || counts.includes(nDefault)) ? nDefault : 0;
  const docCount = positive(doc.default_count, 0);
  const n = preset || (docCount && (!counts.length || counts.includes(docCount)) ? docCount : counts[0] || 4);
  const example = record(doc.example);
  const before = image(example.before_url),
    after = image(example.after_url);
  const labels = strings(example.labels);
  const price = record(doc.price);
  const wishKey = keys.wishes;
  return {
    cats: cats.map(item => item.slice()),
    scenes,
    sets,
    mps,
    fmts,
    plat,
    counts: counts.length ? counts : [4, 8, 12],
    themes: themes.length ? themes : [...new Set(scenes.map(scene => scene.th[0]).filter(Boolean))],
    wishes: chips.length ? chips : strings(doc.wishes).map(chip => [chip, chip]),
    defaults: {
      cat,
      set: text(doc.default_set) || fieldDefault(form, keys.set),
      n,
      plat: pick([fieldDefault(form, keys.platform), text(doc.default_platform)], mps.map(item => item[0])),
      fmt: pick([fieldDefault(form, keys.format), text(doc.default_format)], fmts.map(item => item[0])) || 'mp',
    },
    limits: {
      files: photoSlots(form).slice(0, 5).length,
      scenes: positive(rawField(form, keys.scenes).max_items, positive(doc.max_scenes, 4)),
      wishes: limitOf(wishKey ? fieldOf(form, wishKey) : undefined, positive(doc.wishes_max, 300)),
      side: positive(doc.min_side, 900),
    },
    unit: unitPrice(form, doc, 'pshoot'),
    unitWord: text(price.unit_text) || 'за кадр',
    examples: Object.fromEntries(
      rows(doc.categories).flatMap(item => {
        const ex = record(item.example),
          b = image(ex.before_url),
          a = image(ex.after_url);
        return idOf(item.id) && b && a ? [[idOf(item.id), { before: b, after: a }]] : [];
      }),
    ),
    example: before && after ? { before, after, labels: labels.length === 2 ? labels : ['Ваше фото', 'В сцене'], title: text(example.title), text: text(example.text) } : null,
  };
}

/** Набор категории по id (или null) */
export const setIn = (lists: PshootLists, cat: string, id: string) => (lists.sets[cat] ?? []).find(item => item.id === id) ?? null;
/** Набор по умолчанию категории: default_set документа, иначе первый */
export function defaultSet(lists: PshootLists, cat: string): string {
  const list = lists.sets[cat] ?? [];
  return (setIn(lists, cat, lists.defaults.set) ?? list[0])?.id ?? lists.defaults.set ?? '';
}
/** Сцены вида: свой набор — его сцены, иначе сцены набора; свои фото сцен — нет */
export function viewScenes(lists: PshootLists, v: Pick<PshootView, 'cat' | 'set' | 'own' | 'ref'>): string[] {
  if (v.ref) return [];
  if (v.set === 'own') return v.own.slice();
  return (setIn(lists, v.cat, v.set) ?? setIn(lists, v.cat, defaultSet(lists, v.cat)))?.sc.slice() ?? [];
}

/** Входы → вид. Набор: set_id категории, 'own' — сцены из scenes; без set_id — набор с теми же сценами или «Свой набор»; свои фото сцен — set_id 'ref' */
export function readPshoot(form: StudioForm | null, lists: PshootLists, inputs: Inputs): PshootView {
  const keys = pshootKeys(form);
  const catIds = lists.cats.map(item => item[0]);
  const rawCat = valueOf(form, inputs, keys.category);
  const cat = catIds.includes(rawCat) ? rawCat : lists.defaults.cat;
  const known = new Set(lists.scenes.map(scene => scene.id));
  const listed = (decode<unknown[]>(keys.scenes ? inputs[keys.scenes] : undefined, []) ?? [])
    .map(item => idOf(item && typeof item === 'object' ? (item as Record<string, unknown>).scene_id : item))
    .filter(id => known.has(id));
  const rawSet = valueOf(form, inputs, keys.set);
  const ref = rawSet === 'ref';
  let set = defaultSet(lists, cat);
  let own: string[] = [];
  if (rawSet === 'own') {
    set = 'own';
    own = listed.slice(0, lists.limits.scenes);
  } else if (!ref && rawSet && setIn(lists, cat, rawSet)) set = rawSet;
  else if (!ref && !rawSet && listed.length) {
    const same = (lists.sets[cat] ?? []).find(item => item.sc.join() === listed.join());
    if (same) set = same.id;
    else {
      set = 'own';
      own = listed.slice(0, lists.limits.scenes);
    }
  }
  if (set === 'own' && !own.length) set = defaultSet(lists, cat);
  const count = Number(valueOf(form, inputs, keys.count));
  const plat = valueOf(form, inputs, keys.platform);
  const fmt = valueOf(form, inputs, keys.format);
  return {
    cat,
    set,
    own,
    ref,
    n: lists.counts.includes(count) ? count : lists.defaults.n,
    plat: lists.mps.some(item => item[0] === plat) ? plat : lists.defaults.plat,
    fmt: lists.fmts.some(item => item[0] === fmt) ? fmt : lists.defaults.fmt,
    pick: pickFrom({ model: valueOf(form, inputs, keys.model), person_id: valueOf(form, inputs, keys.personId), avatar_id: valueOf(form, inputs, keys.avatarId) }),
    wishes: valueOf(form, inputs, keys.wishes),
  };
}

/** Значение поля по правилам родной формы (writeField): пустое — ключа нет */
function put(next: Inputs, form: StudioForm | null, key: string | undefined, value: unknown) {
  if (!key) return;
  const encoded = encode(fieldOf(form, key), value);
  if (encoded === undefined || encoded === '' || encoded === '[]') delete next[key];
  else next[key] = encoded;
}

/** Вид → входы поверх prev: только опубликованные ключи. model — выбор ряда «Кто в кадре» (people-bridge modelOf); refs — сколько своих фото
    сцен выбрано (число фото в режиме референса); shoot — inputs.shoot блока «Съёмка» (undefined — ключ не трогать, null — убрать) */
export function writePshoot(form: StudioForm | null, lists: PshootLists, v: PshootView, prev: Inputs, ctx: { model: ModelChoice; refs: number; shoot?: string | null }): Inputs {
  const keys = pshootKeys(form);
  const next = { ...prev };
  /* категория — только опубликованная (родная форма: categoryInput) */
  if (keys.category) {
    if (allowed(fieldOf(form, keys.category), v.cat)) put(next, form, keys.category, v.cat);
    else delete next[keys.category];
  }
  /* набор и сцены пишутся вместе (родная форма: chooseSet, applyScenes); свои фото сцен — set_id 'ref' и без сцен */
  const count = v.ref ? ctx.refs : v.n;
  put(next, form, keys.set, v.ref ? 'ref' : v.set);
  if (keys.scenes) put(next, form, keys.scenes, v.ref ? [] : splitScenes(viewScenes(lists, v), count));
  /* число кадров — в пределах поля (родная форма: countInput); иначе прежнее значение остаётся */
  if (keys.count) {
    const spec = rawField(form, keys.count);
    const min = typeof spec.min === 'number' ? spec.min : 1;
    const max = typeof spec.max === 'number' ? spec.max : Infinity;
    if (count >= min && count <= max && (v.ref || allowed(fieldOf(form, keys.count), String(count)))) put(next, form, keys.count, count);
  }
  if (keys.platform && allowed(fieldOf(form, keys.platform), v.plat)) put(next, form, keys.platform, v.plat);
  if (keys.format && allowed(fieldOf(form, keys.format), v.fmt)) put(next, form, keys.format, v.fmt);
  /* модель (родная форма: pickModel): model, а person_id, avatar_id и me — только у своего выбора */
  if (keys.model) {
    const choice = allowed(fieldOf(form, keys.model), ctx.model.model) ? ctx.model : { model: 'none' as const };
    put(next, form, keys.model, choice.model);
    put(next, form, keys.personId, choice.model === 'person' ? choice.person_id : undefined);
    put(next, form, keys.avatarId, choice.model === 'char' ? choice.avatar_id : undefined);
    put(next, form, keys.me, choice.model === 'me' ? choice.me : undefined);
  }
  put(next, form, keys.wishes, v.wishes);
  if (keys.shoot && ctx.shoot !== undefined) {
    if (ctx.shoot === null || ctx.shoot === '{}') delete next[keys.shoot];
    else next[keys.shoot] = ctx.shoot;
  }
  return next;
}

/** Форма публикует то, без чего экран на движке не работает: слот фото товара */
export function pshootSupported(form: StudioForm | null): boolean {
  return photoSlots(form).length > 0;
}
